// react
import { useEffect, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';

// relative
import BuildingProtectionOccupancyFormFields from './BuildingProtectionOccupancyFormFields';
import InitializationFormFields from './InitializationFormFields';
import PredictiveFrostProtectionFormFields from './PredictiveFrostProtectionFormFields';
import PredictiveOccupancyDetectionFormFields from './PredictiveOccupancyDetectionFormFields';

// Deep-ish clone
const clone = (x) => JSON.parse(JSON.stringify(x ?? {}));

// Build a section object with defaults from spec, overlaid with existing
const buildSectionFromSpec = (specSection, existingSection = {}) => {
  // specSection can be an object of groups (e.g., { general: [...], baseline_model: [...] })
  // or a flat array (e.g., building_protection: [...])
  const out = {};

  if (Array.isArray(specSection)) {
    // flat array case
    for (const field of specSection) {
      const key = field.Key;
      const defaultValue = field.DefaultValue;
      out[key] = existingSection[key] ?? clone(defaultValue);
    }
  } else if (typeof specSection === 'object' && specSection) {
    // grouped case
    for (const group of Object.keys(specSection)) {
      out[group] = {};
      for (const field of specSection[group]) {
        const key = field.Key;
        const defaultValue = field.DefaultValue;
        out[group][key] = existingSection[group]?.[key] ?? clone(defaultValue);
      }
    }
  }

  return out;
};

// Merge all sections from specs into form state, using existing values where present
const hydrateFormState = (specificationData, algorithmInput) => {
  if (!specificationData || !algorithmInput) return {};
  const result = {};
  if (algorithmInput?.predictive_occupancy_detection) {
    result.predictive_occupancy_detection = buildSectionFromSpec(
      specificationData?.predictive_occupancy_detection,
      algorithmInput?.predictive_occupancy_detection,
    );
  }
  if (algorithmInput?.building_protection) {
    result.building_protection = buildSectionFromSpec(
      specificationData?.building_protection,
      algorithmInput?.building_protection,
    );
  }
  if (algorithmInput?.predictive_frost_protection) {
    result.predictive_frost_protection = buildSectionFromSpec(
      specificationData?.predictive_frost_protection,
      algorithmInput?.predictive_frost_protection,
    );
  }
  if (algorithmInput?.initialization) {
    result.initialization = buildSectionFromSpec(
      specificationData?.initialization,
      algorithmInput.initialization,
    );
  }
  return result;
};

const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true; // Handle primitive values

  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return false; // Handle cases where either is not an object or is null
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false; // Different number of keys

  for (let key of keys1) {
    if (!keys2.includes(key)) return false; // Key missing in obj2
    if (!deepEqual(obj1[key], obj2[key])) return false; // Recursive comparison
  }

  return true;
};

const AlgorithmInputSimpleDialog = ({
  open,
  onClose,
  update,
  specificationData,
  subscription,
  algorithmInput,
  updateMachineLearningModel,
  disableAlgorithmInputForm = false,
}) => {
  const initialFormData = hydrateFormState(specificationData, algorithmInput);

  const { t } = useTranslation();

  const [formState, setFormState] = useState(initialFormData);
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [pendingFormState, setPendingFormState] = useState(null); // Holds finalFormState during the dialog
  const [countdown, setCountdown] = useState(5); // Start countdown at 5 seconds
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  // Derive include flags from presence in formState
  const includePredictiveOccupancy = !!formState.predictive_occupancy_detection;
  const includeBuildingProtection = !!formState.building_protection;
  const includePredictiveFrostProtection =
    !!formState.predictive_frost_protection;
  const includeInitialization = !!formState.initialization;

  // Toggle a section by key
  const toggleSection = (sectionKey, specKey, enabled) => {
    setFormState((prev) => {
      const next = { ...prev };
      if (enabled) {
        // add with defaults (keeping any existing values if present)
        next[sectionKey] = buildSectionFromSpec(
          specificationData[specKey],
          prev[sectionKey],
        );
      } else {
        delete next[sectionKey];
      }
      return next;
    });
  };

  const handleSectionSubmit = (section, sectionData) => {
    setFormState((prev) => ({
      ...prev,
      [section]: clone(sectionData),
    }));
  };

  const handleResetOccupancyDetectionModel = async () => {
    const body = {
      reset_predictive_occupancy_detection: true,
      reset_preheat: false,
      reset_cooldown: false,
    };

    if (pendingFormState) {
      try {
        await submitForm(pendingFormState); // Submit the saved form state
        setPromptDialogOpen(false);
      } catch (error) {
        console.error('Failed to submit form:', error);
        return; // Exit the function if submitForm encounters an error
      }
    }

    // Add a delay before calling updateMachineLearningModel
    setTimeout(() => {
      try {
        updateMachineLearningModel({
          id: subscription.JobSubscriptionId,
          data: body,
        });
      } catch (error) {
        console.error('Failed to update machine learning model:', error);
      }
    }, 2000); // 2-second delay
  };

  const openPromptDialog = (finalFormState) => {
    setPendingFormState(finalFormState);
    setCountdown(5);
    setIsButtonDisabled(true);
    setPromptDialogOpen(true);
  };

  const closePromptDialog = () => {
    setPromptDialogOpen(false);
    setCountdown(5);
    setIsButtonDisabled(true);
  };

  useEffect(() => {
    if (!promptDialogOpen) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev > 1) return prev - 1;

        clearInterval(timer);
        setIsButtonDisabled(false);
        return 0;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [promptDialogOpen]);

  const handleSubmit = () => {
    // compute final (everything already represented by presence of keys)
    const finalFormState = clone(formState);

    const initialBaselineModel =
      subscription.AlgorithmInput?.predictive_occupancy_detection
        ?.baseline_model || {};
    const finalBaselineModel =
      finalFormState.predictive_occupancy_detection?.baseline_model || {};

    const baselineModelChanged = !deepEqual(
      initialBaselineModel,
      finalBaselineModel,
    );

    if (baselineModelChanged) {
      openPromptDialog(finalFormState);
      return;
    }

    submitForm(finalFormState);
  };

  const submitForm = (updatedFormState) => {
    const putData = {
      DataInput: subscription.DataInput,
      UserInput: subscription.UserInput,
      AlgorithmInput: updatedFormState,
      DisplayName: subscription.DisplayName,
    };
    try {
      const response = update(putData);
      onClose();
      return response;
    } catch (e) {
      console.error('Failed to update subscription:', e);
    }
  };

  const resetForm = () => {
    setFormState({});
    setCountdown(5);
    setIsButtonDisabled(true);
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {t('glossary.algorithmInput', { ns: 'buildingOptimization' })}
        </DialogTitle>
        <DialogContent dividers>
          {/* Predictive Occupancy */}
          <FormControlLabel
            control={
              <Switch
                checked={includePredictiveOccupancy}
                onChange={(e) =>
                  toggleSection(
                    'predictive_occupancy_detection',
                    'predictive_occupancy_detection',
                    e.target.checked,
                  )
                }
              />
            }
            label="Predictive Occupancy Detection"
          />
          {includePredictiveOccupancy &&
            specificationData?.predictive_occupancy_detection && (
              <PredictiveOccupancyDetectionFormFields
                sectionState={formState.predictive_occupancy_detection}
                predictiveOccupancyDetectionSpecification={
                  specificationData?.predictive_occupancy_detection
                }
                onSectionSubmit={(data) =>
                  handleSectionSubmit('predictive_occupancy_detection', data)
                }
              />
            )}

          <Divider sx={{ my: 2 }} />

          {/* Building Protection */}
          <FormControlLabel
            control={
              <Switch
                checked={includeBuildingProtection}
                onChange={(e) =>
                  toggleSection(
                    'building_protection',
                    'building_protection',
                    e.target.checked,
                  )
                }
              />
            }
            label="Include Building Protection"
          />
          {includeBuildingProtection &&
            specificationData?.building_protection && (
              <BuildingProtectionOccupancyFormFields
                sectionState={formState.building_protection}
                specification={specificationData.building_protection}
                onSectionSubmit={(data) =>
                  handleSectionSubmit('building_protection', data)
                }
              />
            )}

          <Divider sx={{ my: 2 }} />

          {/* Frost Protection */}
          <FormControlLabel
            control={
              <Switch
                checked={includePredictiveFrostProtection}
                onChange={(e) =>
                  toggleSection(
                    'predictive_frost_protection',
                    'predictive_frost_protection',
                    e.target.checked,
                  )
                }
              />
            }
            label="Include Predictive Frost Protection"
          />
          {includePredictiveFrostProtection &&
            specificationData?.predictive_frost_protection && (
              <PredictiveFrostProtectionFormFields
                sectionState={formState.predictive_frost_protection}
                specification={specificationData.predictive_frost_protection}
                onSectionSubmit={(data) =>
                  handleSectionSubmit('predictive_frost_protection', data)
                }
              />
            )}

          <Divider sx={{ my: 2 }} />

          {/* Initialization */}
          <FormControlLabel
            control={
              <Switch
                checked={includeInitialization}
                onChange={(e) =>
                  toggleSection(
                    'initialization',
                    'initialization',
                    e.target.checked,
                  )
                }
              />
            }
            label="Include Initialization"
          />
          {includeInitialization && specificationData?.initialization && (
            <InitializationFormFields
              sectionState={formState.initialization}
              specification={specificationData?.initialization}
              onSectionSubmit={(data) =>
                handleSectionSubmit('initialization', data)
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            variant="text"
            disabled={disableAlgorithmInputForm}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={disableAlgorithmInputForm}
          >
            {t('actions.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={promptDialogOpen}
        onClose={() => setPromptDialogOpen(false)}
      >
        <DialogTitle>
          {t('algorithmInputSimpleDialog.prompt.title', {
            ns: 'buildingOptimization',
          })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('algorithmInputSimpleDialog.prompt.message', {
              ns: 'buildingOptimization',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              color="warning"
              onClick={handleResetOccupancyDetectionModel}
              disabled={isButtonDisabled}
              sx={{ textTransform: 'none' }}
            >
              {isButtonDisabled
                ? `Wait ${countdown}s`
                : 'Reset Occupancy Detection Model'}
            </Button>
            <Box>
              <Button
                sx={{ textTransform: 'none' }}
                onClick={closePromptDialog}
              >
                {t('actions.cancel')}
              </Button>
              <Button
                onClick={() => {
                  closePromptDialog();
                  if (pendingFormState) submitForm(pendingFormState);
                }}
                sx={{ ml: 1, textTransform: 'none' }}
                variant="contained"
              >
                {t('algorithmInputSimpleDialog.prompt.confirmWithoutReset', {
                  ns: 'buildingOptimization',
                })}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AlgorithmInputSimpleDialog;
